import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import colors from 'common/styles/colors';
import { handleResponse } from 'common/helpers';
import { axiosInstance, ENDPOINT } from 'common/constants';
import { userActions } from 'onboarding/user.actions';
import useOrgWidgetData from '../hooks/useOrgWidgetData';
import { navigateToSite } from '../utils/orgSiteNavigate';
import OrgWidgetFrame from './OrgWidgetFrame';
import OrgSiteName from './OrgSiteName';
import { OrgWidgetLoading, OrgWidgetEmpty } from './OrgWidgetStates';
import { getOrgWidgetTitle, useResolveOrgWidgetMeta } from './orgWidgetMeta';

const WIDGET_NAME = 'org_site_scorecard';

const Wrapper = styled.div`
  height: 100%;
  min-height: 0;
  overflow: auto;
  padding: 4px 10px 10px;
  box-sizing: border-box;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: 'Petasense Open Sans', sans-serif;
`;

const Th = styled.th`
  padding: 8px 10px;
  text-align: ${props => (props.align === 'right' ? 'right' : 'left')};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.greyXD};
  border-bottom: 1px solid ${colors.greyL};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Td = styled.td`
  padding: 10px;
  text-align: ${props => (props.align === 'right' ? 'right' : 'left')};
  font-size: 12px;
  font-weight: ${props => (props.emphasis ? 700 : 600)};
  color: ${props => props.tone || colors.greyXXD};
  border-bottom: 1px solid ${colors.greyXL};
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* The whole row opens the site, so the name and caret are only the affordance —
   every cell is a click target. */
const Tr = styled.tr`
  cursor: ${props => (props.interactive ? 'pointer' : 'default')};

  &:last-child td {
    border-bottom: none;
  }

  &:hover td {
    background: ${colors.greyXXXL};
  }

  ${props => (props.interactive ? `
    &:hover .org-site-name {
      color: #1a73e8;
      text-decoration: underline;
    }
    &:hover .org-site-caret {
      opacity: 1;
      color: #1a73e8;
    }
    /* Focus lands on the name button, so mirror the hover state onto the row
       to show keyboard users which row they are about to open. */
    &:focus-within td {
      background: ${colors.greyXXXL};
    }
    &:focus-within .org-site-caret {
      opacity: 1;
    }
  ` : '')}
`;

const scorecardRowsFromData = (data) => {
  if (!data) return [];
  if (data.items && data.items.length) return data.items;
  if (data.site_breakdown && data.site_breakdown.length) return data.site_breakdown;
  return [];
};

const formatPct = (value) => {
  if (value == null) return '—';
  return `${value}%`;
};

const formatCount = value => (value != null ? value : '—');

const toneForPct = (value, { invert = false } = {}) => {
  if (value == null) return colors.greyXD;
  if (invert) {
    if (value >= 20) return colors.red;
    if (value > 0) return colors.orange;
    return colors.greyXXD;
  }
  if (value >= 90) return colors.greenD;
  if (value < 70) return colors.orange;
  return colors.greyXXD;
};

const toneForCount = (value) => {
  if (value == null) return colors.greyXD;
  if (value > 0) return colors.red;
  return colors.greyXXD;
};

const resolveUnreachablePct = (site) => {
  if (site.device_unreachable_pct != null) return site.device_unreachable_pct;
  return null;
};

const OrgSiteScorecard = ({ organisationId, widgetsMetadata, title, selectAccount }) => {
  const { t } = useTranslation();
  const meta = useResolveOrgWidgetMeta(widgetsMetadata, WIDGET_NAME);
  const widgetTitle = getOrgWidgetTitle(title, meta);
  const { data, loading, error } = useOrgWidgetData(organisationId, meta);
  const [fallbackRows, setFallbackRows] = useState([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);

  const widgetRows = scorecardRowsFromData(data);
  const rows = widgetRows.length ? widgetRows : fallbackRows;

  useEffect(() => {
    if (loading || error || widgetRows.length || !organisationId) {
      setFallbackRows([]);
      return undefined;
    }

    let cancelled = false;
    setFallbackLoading(true);
    handleResponse(axiosInstance.get(ENDPOINT.GET_ORGANISATION_ACCOUNTS(organisationId)))
      .then((res) => {
        if (cancelled) return;
        const items = (res && res.items) || [];
        setFallbackRows(items.map(site => ({
          account_id: site.id,
          name: site.name,
          machines: null,
          good_pct: null,
          critical: null,
          open_events: null,
          device_unreachable_pct: null
        })));
      })
      .catch(() => {
        if (!cancelled) setFallbackRows([]);
      })
      .finally(() => {
        if (!cancelled) setFallbackLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loading, error, organisationId, widgetRows.length]);

  if (loading || fallbackLoading) {
    return (
      <OrgWidgetFrame title={widgetTitle}>
        <Wrapper>
          <OrgWidgetLoading height="120px" />
        </Wrapper>
      </OrgWidgetFrame>
    );
  }

  if (!rows.length) {
    return (
      <OrgWidgetFrame title={widgetTitle}>
        <Wrapper>
          <OrgWidgetEmpty
            message={t('orgView.scorecard.noSites', 'No sites available for this organisation.')}
          />
        </Wrapper>
      </OrgWidgetFrame>
    );
  }

  return (
    <OrgWidgetFrame title={widgetTitle}>
      <Wrapper>
        <Table>
          <thead>
            <tr>
              <Th>{t('orgView.scorecard.site', 'Site')}</Th>
              <Th align="right">{t('orgView.scorecard.machines', 'Assets')}</Th>
              <Th align="right">{t('orgView.scorecard.goodHealth', 'Healthy assets')}</Th>
              <Th align="right">{t('orgView.scorecard.critical', 'Critical assets')}</Th>
              <Th align="right">{t('orgView.scorecard.openEvents', 'Open events')}</Th>
              <Th align="right">{t('orgView.scorecard.deviceUnreachable', 'Device unreachability')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((site) => {
              const unreachablePct = resolveUnreachablePct(site);
              const navigable = site.account_id != null;
              const go = () => navigateToSite(selectAccount, site.account_id, null);
              return (
                <Tr
                  key={site.account_id || site.name}
                  title={site.name}
                  interactive={navigable}
                  onClick={navigable ? go : undefined}
                >
                  <Td>
                    <OrgSiteName name={site.name} navigable={navigable} />
                  </Td>
                  <Td align="right">{formatCount(site.machines)}</Td>
                  <Td align="right" tone={toneForPct(site.good_pct)}>
                    {formatPct(site.good_pct)}
                  </Td>
                  <Td align="right" tone={toneForCount(site.critical)} emphasis={site.critical > 0}>
                    {formatCount(site.critical)}
                  </Td>
                  <Td align="right" tone={toneForCount(site.open_events)} emphasis={site.open_events > 0}>
                    {formatCount(site.open_events)}
                  </Td>
                  <Td align="right" tone={toneForPct(unreachablePct, { invert: true })}>
                    {formatPct(unreachablePct)}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </Wrapper>
    </OrgWidgetFrame>
  );
};

OrgSiteScorecard.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string,
  selectAccount: PropTypes.func.isRequired
};

OrgSiteScorecard.defaultProps = {
  widgetsMetadata: null,
  title: null
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      selectAccount: userActions.selectAccount
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(OrgSiteScorecard);
